import React from 'react'
import {ErrorMessage, Field, Form, Formik} from 'formik'
import * as Yup from 'yup'
import {Input, Switch} from '../../../../_metronic/_partials/controls'
import {SubmitForm, validatePhoneField} from '../../../services/Functional'
import PhoneInput from 'react-phone-input-2'
import {serialize} from 'object-to-formdata'
import Select from 'react-select'
import GeoapifyGeocoderAutocompleteComponent from '../../auth/components/autocomplete/GeoapifyAutocompleteComponent'
import {customSelectHoverStyles} from '../../../services/Styles'
import FAIcon from '../../../../_metronic/helpers/components/FAIcon'
import {faImage, faVideo} from '@fortawesome/free-solid-svg-icons'

let createValidateSchema = {
  subscription_plan: Yup.string().required('Subscription plan is required'),
}

let commonValidateSchema = {
  name: Yup.string().required('Company name is required'),
  description: Yup.string()
    .nullable()
    .min(10, 'Company description must be of minimum of 10 characters')
    .max(1000, 'Company description must be of maximum of 1000 characters'),
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Email is required'),
  contact_person: Yup.string().required('Contact person is required').nullable(),
  location: Yup.string().required('Location is required'),
  phone: Yup.string()
    .min(8, 'Phone must be at least 8 digits')
    .required('Phone is required')
    .test('verifyPhoneValidation', 'Phone number is not valid', function (value) {
      const {country_code} = this.parent as {country_code: string}
      return validatePhoneField(value || '', country_code)
    }),
  logo: Yup.mixed()
    .nullable()
    .test('fileType', 'Only JPG, JPEG, and PNG formats are allowed', (value) => {
      if (!value || typeof value === 'string') return true

      const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png']

      if (value instanceof File) {
        return allowedFormats.includes(value.type)
      }

      return false
    })
    .test('fileSize', 'Logo must be less than 2MB', (value) => {
      if (!value || typeof value === 'string') return true

      if (value instanceof File) {
        return value.size <= 2 * 1024 * 1024
      }

      return false
    }),
  video: Yup.mixed()
    .nullable()
    .test('fileType', 'Only MP4, WebM, and Ogg formats are allowed', (value) => {
      if (!value || typeof value === 'string') return true

      const allowedFormats = ['video/mp4', 'video/webm', 'video/ogg']

      if (value instanceof File) {
        return allowedFormats.includes(value.type)
      }

      return false
    })
    .test('fileSize', 'Video must be less than 50MB', (value) => {
      if (!value || typeof value === 'string') return true

      if (value instanceof File) {
        return value.size <= 50 * 1024 * 1024
      }

      return false
    }),
}

const InspectionCreateCompanySchema = Yup.object().shape(
  Object.assign(createValidateSchema, commonValidateSchema)
)

const InspectionEditCompanySchema = Yup.object().shape(commonValidateSchema)

export const CustomForm = ({
  id,
  inspectionCompany,
  saveInspectionCompany,
  subscriptions,
  actionsLoading,
  isCompanyProfile,
}: any) => {
  const [selectedOption, setSelectedOption] = React.useState(null)

  React.useEffect(() => {
    if (inspectionCompany.id && subscriptions) {
      let option = subscriptions.filter(function (group: any) {
        return parseInt(group.value) === parseInt(inspectionCompany.subscription)
      })
      if (option.length > 0) {
        setSelectedOption(option[0])
      }
    }
  }, [inspectionCompany, subscriptions])

  const isPaymentPending = () => {
    if (
      (inspectionCompany?.plans?.[0]?.payment_status === 'pending' &&
        inspectionCompany?.plans?.[0]?.subscription?.type === 'free') ||
      (inspectionCompany?.plans?.[0]?.payment_status === 'pending' &&
        inspectionCompany?.plans?.[0]?.subscription?.type !== 'paid') ||
      (inspectionCompany?.plans?.[0]?.subscription?.type === 'paid' &&
        inspectionCompany?.plans?.[0]?.payment_status === 'paid')
    ) {
      return true
    }
    return false
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={inspectionCompany}
        validationSchema={id ? InspectionEditCompanySchema : InspectionCreateCompanySchema}
        onSubmit={(values) => {
          const formData = serialize(values)
          saveInspectionCompany(formData)
        }}
      >
        {({values, handleSubmit, touched, errors, handleBlur, handleChange, setFieldValue}) => (
          <>
            <Form className='form'>
              <div className='card gutter-b'>
                <div className='card-body'>
                  <div className='row'>
                    {!isCompanyProfile && (
                      <div className='col-md-12'>
                        <div className='form-group'>
                          <Field
                            component={Switch}
                            value='1'
                            isSelected={values?.is_active && isPaymentPending()}
                            name='is_active'
                            label='Status'
                            isDisabled={inspectionCompany?.is_expired} 
                          />
                        </div>
                      </div>
                    )}
                    {!id && (
                      <div className='col-md-4'>
                        <div className='form-group'>
                          <label className='form-label'>
                            Subscription Plan <span className='text-danger'>*</span>
                          </label>
                          <Select
                            options={subscriptions}
                            value={selectedOption}
                            name='subscription_plan'
                            isDisabled={!!id}
                            classNamePrefix='react-custom-select'
                            styles={customSelectHoverStyles}
                            onChange={(option: any) => {
                              let event = {
                                target: {
                                  name: 'subscription_plan',
                                  value: option.value,
                                },
                              }
                              handleChange(event)
                              setSelectedOption(option)
                            }}
                            onBlur={() => {
                              handleBlur({target: {name: 'subscription_plan'}})
                            }}
                          />
                          <ErrorMessage
                            name='subscription_plan'
                            component='div'
                            className='text-danger mt-2'
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className='row'>
                    <div className='col-md-4'>
                      <div className='form-group'>
                        <label className='form-label'>
                          Company Name <span className='text-danger'>*</span>
                        </label>
                        <Field name='name' component={Input} placeholder='Enter Company Name' />
                        <ErrorMessage name='name' component='div' className='text-danger mt-2' />
                      </div>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-md-12'>
                      <label className='form-label'>Company Description</label>
                      <div className='d-flex align-items-center position-relative pb-3'>
                        <Field
                          name='description'
                          as='textarea'
                          spellCheck={true}
                          placeholder='Enter Description'
                          className='form-control'
                          rows={3}
                        />
                        <span className='position-absolute description-count'>
                          {`${values?.description?.length || 0}/${1000}`} (Min: 10)
                        </span>
                      </div>
                      <ErrorMessage
                        name='description'
                        component='div'
                        className='text-danger mt-2'
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className='card gutter-b'>
                <div className='card-body'>
                  <div className='row'>
                    <div className='col-md-4'>
                      <div className='form-group'>
                        <label className='form-label'>
                          Contact Person <span className='text-danger'>*</span>
                        </label>
                        <Field
                          name='contact_person'
                          component={Input}
                          placeholder='Enter Contact Person'
                        />
                        <ErrorMessage
                          name='contact_person'
                          component='div'
                          className='text-danger mt-2'
                        />
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='form-group'>
                        <label className='form-label' htmlFor='email'>
                          Email <span className='text-danger'>*</span>
                        </label>
                        <input
                          type='email'
                          className='form-control'
                          id='email'
                          name='email'
                          placeholder='Enter Email'
                          disabled={!!id}
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <ErrorMessage name='email' component='div' className='text-danger mt-2' />
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <label className='form-label'>
                        Phone <span className='text-danger'>*</span>
                      </label>
                      <PhoneInput
                        inputClass='w-100'
                        buttonClass='flag_dropdown'
                        dropdownStyle={{backgroundColor: 'none'}}
                        enableSearch={true}
                        disableSearchIcon={true}
                        searchStyle={{width: '90%', margin: '0'}}
                        countryCodeEditable={false}
                        disableDropdown={!!id}
                        country='in'
                        value={inspectionCompany.country_code + inspectionCompany.phone}
                        inputProps={{
                          name: 'phone',
                          id: 'phone',
                          required: true,
                          disabled: !!id,
                        }}
                        onChange={(phone: any, data: any) => {
                          handleChange({
                            target: {
                              name: 'country_code',
                              value: data.dialCode,
                            },
                          })
                          let phone_number = phone.slice(data.dialCode.length)
                          handleChange({
                            target: {
                              name: 'phone',
                              value: phone_number,
                            },
                          })
                        }}
                        onBlur={() => {
                          handleBlur({target: {name: 'phone'}})
                        }}
                      />
                      <ErrorMessage name='phone' component='div' className='text-danger mt-2' />
                    </div>
                    <div className='col-md-4'>
                      <label className='form-label'>Fax</label>
                      <Field name='fax' component={Input} placeholder='Enter Fax' />
                    </div>
                  </div>
                </div>
              </div>
              <div className='card gutter-b'>
                <div className='card-body'>
                  <div className='row'>
                    <div className='col-md-6'>
                      <div className='form-group'>
                        <label className='form-label'>
                          Location <span className='text-danger'>*</span>
                        </label>
                        <GeoapifyGeocoderAutocompleteComponent
                          values={inspectionCompany?.location}
                          placeholder='Type here to find address.'
                          handleChange={(place: any) => {
                            const updateLocationFields = (formatted = '', lat = '', lon = '') => {
                              handleChange({
                                target: {name: 'location', value: formatted},
                              })
                              handleChange({
                                target: {name: 'latitude', value: lat.toString()},
                              })
                              handleChange({
                                target: {name: 'longitude', value: lon.toString()},
                              })
                            }
                            if (place && place.properties && place.properties.formatted) {
                              const {formatted, lat, lon} = place.properties
                              updateLocationFields(formatted, lat, lon)
                            } else {
                              updateLocationFields()
                            }
                          }}
                        />
                        <ErrorMessage
                          name='location'
                          component='div'
                          className='text-danger mt-2'
                        />
                      </div>
                    </div>
                    <div className='col-md-3'>
                      <label className='form-label'>Latitude</label>
                      <Field name='latitude' component={Input} placeholder='Enter Latitude' />
                    </div>
                    <div className='col-md-3'>
                      <label className='form-label'>Longitude</label>
                      <Field name='longitude' component={Input} placeholder='Enter Longitude' />
                    </div>
                  </div>
                </div>
              </div>
              <div className='card gutter-b'>
                <div className='card-body'>
                  <div className='row'>
                    <div className='col-md-4'>
                      <div className='d-flex justify-content-between'>
                        <label className='form-label' htmlFor='logo'>
                          Logo
                        </label>
                        {values.logo && typeof values.logo === 'string' && (
                          <a href={values.logo} rel='noreferrer' target='_blank'>
                            <FAIcon icon={faImage} className='fa-lg' />
                          </a>
                        )}
                      </div>
                      <input
                        type='file'
                        className='form-control'
                        id='logo'
                        accept='image/*'
                        name='logo'
                        onChange={(event) => {
                          // @ts-ignore
                          setFieldValue('logo', event.target.files[0])
                        }}
                      />
                      <ErrorMessage name='logo' component='div' className='text-danger mt-2' />
                    </div>
                    <div className='col-md-4'>
                      <div className='d-flex justify-content-between'>
                        <label className='form-label' htmlFor='video'>
                          Video
                        </label>
                        {values.video && typeof values.video === 'string' && (
                          <a href={values.video} rel='noreferrer' target='_blank'>
                            <FAIcon icon={faVideo} className='fa-lg' />
                          </a>
                        )}
                      </div>
                      <input
                        type='file'
                        className='form-control'
                        id='video'
                        accept='video/*'
                        name='video'
                        onChange={(event) => {
                          // @ts-ignore
                          setFieldValue('video', event.target.files[0])
                        }}
                      />
                      <input type='hidden' name='old_video' value={values.old_video} />
                      <ErrorMessage name='video' component='div' className='text-danger mt-2' />
                    </div>
                  </div>
                </div>
              </div>
              <SubmitForm handleSubmit={handleSubmit} values={values} disabled={actionsLoading} />
            </Form>
          </>
        )}
      </Formik>
    </>
  )
}
